import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dimensions, Linking, Platform, Text } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { AgentDetails } from '@zet/customer'
import { useSecondaryPhoneNumberResendOtp } from '@zet/customer/src/api/hooks/useSecondaryPhoneNumberResendOtp'
import {
  B1,
  B2,
  BottomSheet,
  Button,
  H5,
  Pressable,
  SH1,
  SH3,
  Stack,
  XStack,
} from '@zet/design-system'
import Close from '@zet/design-system/svg/Close'
import { i18n } from '@zet/localization'
import { isApp } from '@zet/utils'
import { styled } from 'nativewind'
import WebView from 'react-native-webview'

import { useGetNextStateForProductFlow } from '../../../../api/brands/hooks'
import { BottomsheetTypes } from '../../../../api/brands/types'
import { useProductFlowApply } from '../../../../hooks/useProductFlowApply'
import {
  privacyPolicyForExperianLink,
  termsAndConditionsForExperianLink,
} from '../../../../utils/constants'
import { Header } from '../header'
import OtpInput from '../otpVerification/otpInput'

const BoldText = styled(Text, 'font-[Graphie-Bold] text-sm leading-5')

const OtpSecondaryPhVerification = ({
  agentData,
  handleBottomsheetState,
  data,
}: {
  agentData: AgentDetails
  handleBottomsheetState: (state: BottomsheetTypes, data?: any) => void
  data: any
}) => {
  function closeBottomSheet() {
    handleBottomsheetState(null)
  }
  const { height } = Dimensions.get('window')
  const [disableResend, setDisableResend] = useState<boolean>(false)
  const [value, setValue] = useState({})
  const [timer, setTimer] = useState<number>(30)
  const [showTnCBottomSheet, setShowTnCBottomSheet] = useState(false)
  const [pdfData, setPdfData] = useState({ link: '', title: '' })
  const timerRef = useRef(0)
  const { mutate, isLoading } = useGetNextStateForProductFlow()
  const toast = useToast()
  const { apply, shareLinkLoading } = useProductFlowApply(
    agentData,
    handleBottomsheetState,
  )
  const { mutate: mutateResendOtp } = useSecondaryPhoneNumberResendOtp()

  const interval = 1000
  let prev = 0
  let diff = 0

  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }

  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  const handleButtonDisablity = () => {
    const isValueLengthValid = Object.values(value).join('').length === 4

    if (!isValueLengthValid) {
      return true
    }
  }

  function resendOtp() {
    publishEvent('click_action', {
      item_name: 'shortflow_secondary_otp_verification_resend',
    })
    mutateResendOtp(
      {
        customerId: Number(sessionStorage.getItem('customerId')),
        isApp: isApp,
        secondaryPhoneNumber: data?.secondaryPhoneNumber,
      },
      {
        onSuccess: (data) => {
          if (data?.actionData?.lockedTill) {
            setDisableResend(true)
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description: data?.actionData?.reason,
              hideAfter: 3000,
            })
            return data?.actionData
          }
        },
      },
    )
  }

  const onDismiss = () => {
    setShowTnCBottomSheet(false)
  }

  function openTermsAndConditions() {
    setShowTnCBottomSheet(true)
    setPdfData({
      link: termsAndConditionsForExperianLink,
      title: i18n.t('verifyPhNum.terms'),
    })
  }

  function openPrivacyPolicy() {
    setShowTnCBottomSheet(true)
    setPdfData({
      link: privacyPolicyForExperianLink,
      title: i18n.t('verifyPhNum.privacyPolicy'),
    })
  }

  const handleSubmit = () => {
    publishEvent('click_action', {
      item_name: 'shortflow_secondary_number_otp',
    })
    mutate(
      {
        customerPayload: {
          customerId: sessionStorage.getItem('customerId') as string,
          otp: Object.values(value).join(''),
          secondaryPhoneNumber: String(data?.secondaryPhoneNumber),
        },
        userState: 'SHOWN_SECONDARY_OTP_SCREEN_V3',
      },
      {
        onSuccess(data) {
          if (data?.code === 400) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description:
                data?.message || i18n.t('toastMessages.somethingWentWrong'),
              hideAfter: 3000,
            })
          }
          if (data?.actionData?.lockedTill) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description: data?.actionData?.reason,
              hideAfter: 3000,
            })
            return data?.actionData
          }
          if (data?.action === 'SHOW_PAN_COLLECTION_SCREEN_V3') {
            handleBottomsheetState('BASIC_DETAILS')
          } else if (data?.action === 'SHOW_BASIC_DETAILS_SCREEN_V3') {
            handleBottomsheetState('EMPLOYMENT_DETAILS')
          } else if (data?.action === 'REDIRECTION_URL') {
            closeBottomSheet()
            if (Platform.OS === 'web') {
              window.location.href = data?.actionData?.url
            } else {
              Linking.openURL(data?.actionData?.url)
            }
          } else if (data?.action === 'SHOW_RECOMMENDATION_SCREEN') {
            apply()
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      },
    )
  }

  useEffect(() => {
    otpTimer()
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  const editPhoneNumber = () => {
    mutate(
      {
        customerPayload: {
          customerId: sessionStorage.getItem('customerId') as string,
        },
        userState: 'BACK',
      },
      {
        onSuccess(data) {
          if (data?.action === 'SHOW_SECONDARY_PHONE_NUMBER_SCREEN_V3') {
            handleBottomsheetState('SECONDARY_PHONE_NUMBERS', data)
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      },
    )
  }

  return (
    <>
      <Header
        closeBottomSheet={closeBottomSheet}
        title={i18n.t('verifySecondaryPhNum.otpVerification')}
      />
      <Stack className="my-3 px-3">
        <B1>{i18n.t('verifySecondaryPhNum.otpSentToSecondary')}</B1>
        <XStack>
          <SH1>{data?.secondaryPhoneNumber}</SH1>
          <Pressable onPress={editPhoneNumber}>
            <SH1 className="text-primary-100 pl-2 underline">
              {i18n.t('verifySecondaryPhNum.edit')}
            </SH1>
          </Pressable>
        </XStack>
        <Stack className="mb-2 pt-3">
          <OtpInput value={value} setValue={setValue} otpCount={4} />

          <Stack className="items-center">
            {timer === 0 || disableResend ? (
              <Pressable
                onPress={() => {
                  prev = 0
                  diff = 0
                  timerFunction()
                  resendOtp()
                }}
              >
                <SH3 className="text-grays-60">
                  {i18n.t('webFlow.otpScreen.didNot')}
                  <SH3 className="text-primary-100 font-[Graphie-Bold]">
                    {i18n.t('webFlow.otpScreen.resend')}
                  </SH3>
                </SH3>
              </Pressable>
            ) : (
              <B2 className=" text-grays-60 text-center">
                {`Resend OTP in `}
                <SH3 className="text-primary-100">
                  00:{timer < 10 ? `0${timer}` : timer}
                </SH3>
              </B2>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack className="bg-neutral-white100 absolute bottom-0 w-full px-3 py-2">
        <Stack className="my-3">
          <B2 className="">
            {i18n.t('webFlow.otpScreen.byClicking')}
            <Pressable
              onPress={openTermsAndConditions}
              className=" ml-1 justify-center"
            >
              <BoldText>{i18n.t('verifyPhNum.terms')}</BoldText>
            </Pressable>
            <B2> {i18n.t('verifyPhNum.and')} </B2>

            <Pressable className="justify-center" onPress={openPrivacyPolicy}>
              <BoldText>{i18n.t('verifyPhNum.privacyPolicy')}.</BoldText>
            </Pressable>
          </B2>
        </Stack>
        <Button
          className="bg-tertiary-200 active:bg-tertiary-200 hover:bg-tertiary-200  w-full"
          disabled={handleButtonDisablity()}
          onPress={handleSubmit}
          size={'lg'}
          loading={isLoading || shareLinkLoading}
        >
          {i18n.t('bottomView.continue')}
        </Button>
      </Stack>
      <BottomSheet
        visible={showTnCBottomSheet}
        onDismiss={onDismiss}
        snapPoints={[700, 700]}
      >
        <Stack style={{ height: height }}>
          <XStack className="border-grays-30 items-center justify-between border-b px-3 py-2">
            <H5>{pdfData?.title}</H5>
            <Pressable onPress={onDismiss}>
              <Close />
            </Pressable>
          </XStack>
          <WebView
            source={{ uri: pdfData?.link }}
            androidHardwareAccelerationDisabled
          />
        </Stack>
      </BottomSheet>
    </>
  )
}

export default OtpSecondaryPhVerification
